// InkCanvas.jsx — opaque ink surface designed to be recorded as video.
// Same behavior as before the port — just a real ES module.
import React from 'react';

export function InkCanvas({ canvasRef, penColor = "#1a1a1a", penWidth = 2 }) {
  const wrapRef = React.useRef(null);
  const canvasEl = React.useRef(null);
  const strokesRef = React.useRef([]);
  const currentRef = React.useRef([]);
  const drawingRef = React.useRef(false);
  const hasDrawnRef = React.useRef(false);

  const drawStroke = (ctx, stroke) => {
    if (!stroke || stroke.length < 1) return;
    ctx.strokeStyle = stroke[0].color;
    ctx.lineWidth = stroke[0].width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(stroke[0].x, stroke[0].y);
    for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
    if (stroke.length === 1) ctx.lineTo(stroke[0].x + 0.1, stroke[0].y + 0.1);
    ctx.stroke();
  };

  const redraw = React.useCallback(() => {
    const canvas = canvasEl.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth || canvas.width / dpr;
    const h = canvas.clientHeight || canvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "rgba(173,89,5,0.10)";
    ctx.lineWidth = 0.5;
    for (let x = 0; x < w; x += 24) {
      ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke();
    }
    for (let y = 0; y < h; y += 24) {
      ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke();
    }
    for (const s of strokesRef.current) drawStroke(ctx, s);
  }, []);

  const resize = React.useCallback(() => {
    if (drawingRef.current) return;
    const canvas = canvasEl.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = wrap.clientWidth;
    const cssH = wrap.clientHeight;
    const targetW = Math.max(1, Math.floor(cssW * dpr));
    const targetH = Math.max(1, Math.floor(cssH * dpr));
    if (canvas.width !== targetW) canvas.width = targetW;
    if (canvas.height !== targetH) canvas.height = targetH;
    canvas.style.width = cssW + "px";
    canvas.style.height = cssH + "px";
    redraw();
  }, [redraw]);

  React.useEffect(() => {
    resize();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (ro) ro.disconnect();
    };
  }, [resize]);

  React.useImperativeHandle(canvasRef, () => ({
    getCanvas: () => canvasEl.current,
    toDataURL: (...a) => canvasEl.current.toDataURL(...a),
    captureStream: (fps = 15) => canvasEl.current.captureStream(fps),
    clear: () => { strokesRef.current = []; hasDrawnRef.current = false; redraw(); },
    undo: () => { strokesRef.current.pop(); redraw(); },
    hasDrawn: () => hasDrawnRef.current,
    // v2 needs to force a paint before captureStream so the backing store
    // isn't empty when MediaRecorder samples the first frame. resize() is
    // idempotent and cheap — call it to guarantee dimensions + a fresh
    // redraw() with white background + grid.
    ensurePainted: () => { resize(); redraw(); },
  }), [redraw, resize]);

  const getPos = (e) => {
    const rect = canvasEl.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    try { canvasEl.current.setPointerCapture(e.pointerId); } catch (_) {}
    drawingRef.current = true;
    const pos = getPos(e);
    currentRef.current = [{ ...pos, color: penColor, width: penWidth }];
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const stroke = currentRef.current;
    const prev = stroke[stroke.length - 1];
    stroke.push({ ...pos, color: penColor, width: penWidth });
    const ctx = canvasEl.current.getContext("2d");
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const onPointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try { canvasEl.current.releasePointerCapture(e.pointerId); } catch (_) {}
    const stroke = currentRef.current;
    if (stroke.length >= 1) {
      strokesRef.current.push(stroke);
      hasDrawnRef.current = true;
    }
    currentRef.current = [];
  };

  return (
    <div ref={wrapRef} className="ink-wrap">
      <canvas
        ref={canvasEl}
        className="ink-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}
